import {
  CARE_PLAN_SERVICE_KEY,
  CWF_DOCUMENT_STATUS,
  CWF_DOCUMENT_TYPE_NAMES,
  CwfActiveDocument,
  CwfDocumentStatus,
  CwfDocumentType,
  CwfDocumentTypeName,
  PoetryMetadata,
  ServicesInfo,
  Sku,
} from '@/api';
import { StudyResult } from '@/shared/model';
import { generateAppRoute } from '@/shared/util';

import { CWF_DOCS_TYPES_MAP } from '../../../../constant';
import { PatientDocLink } from '../patient-docs-side-panel.model';
import { DocsByStudy, DocsByStudyId } from './patient-docs.model';

const NON_VIEWABLE_STATUSES: CwfDocumentStatus[] = [
  CWF_DOCUMENT_STATUS.INTERNAL_REJECTED,
  CWF_DOCUMENT_STATUS.INITIAL_CHECK,
];

function generateMhsLink(id: string): PatientDocLink {
  return {
    studyId: id,
    type: 'MHS',
    url: generateAppRoute('MHS_DOCUMENT', { params: { studyId: id } }),
  };
}

function generateRadiologistReportPdfLink(id: string): PatientDocLink {
  return {
    studyId: id,
    type: 'RADIOLOGY_REPORT',
    url: generateAppRoute('RADIOLOGY_REPORT_DOCUMENT', { params: { studyId: id } }),
  };
}

export function generateCwfDocumentLink(document: CwfActiveDocument): PatientDocLink {
  const { careActivityId, carePlanId, studyId, typeId } = document;

  if (careActivityId && carePlanId) {
    return {
      careActivityId,
      carePlanId,
      studyId: null,
      type: 'CWF',
      typeId,
      url: generateAppRoute('CWF_DOCUMENT_CARE_PLAN', {
        params: {
          careActivityId,
          carePlanId,
          typeId,
        },
      }),
    };
  }

  if (!studyId) {
    throw new Error('Document must have studyId or both careActivityId and carePlanId');
  }

  return {
    careActivityId: null,
    carePlanId: null,
    studyId,
    type: 'CWF',
    typeId,
    url: generateAppRoute('CWF_DOCUMENT', {
      params: { studyId, typeId },
    }),
  };
}

interface DetermineAcceptedTypeIdsArgs {
  servicesInfo: ServicesInfo[];
  sku: Sku;
  types: CwfDocumentType[];
}

export function determineAcceptedTypeIds({
  servicesInfo,
  sku,
  types,
}: DetermineAcceptedTypeIdsArgs) {
  return types
    .filter(({ name }) =>
      sku ? (CWF_DOCS_TYPES_MAP[sku] ?? []).includes(name as CwfDocumentTypeName) : false,
    )
    .map(({ uuid }) => uuid)
    .filter((typeId) => {
      if (sku === '900') {
        const hasAdvancedHeartServiceCode = servicesInfo.some(
          (service) => service.code === CARE_PLAN_SERVICE_KEY.IMAGING_BASE_ADVANCE_HEART,
        );

        const hasAdvancedNeuroServiceCode = servicesInfo.some(
          (service) => service.code === CARE_PLAN_SERVICE_KEY.IMAGING_BASE_ADVANCE_NEURO,
        );

        const hasBodyCompositionServiceCode = servicesInfo.some(
          (service) => service.code === CARE_PLAN_SERVICE_KEY.IMAGING_BASE_BODY_COMPOSITION,
        );

        if (typeId === CWF_DOCUMENT_TYPE_NAMES.ADVANCE_HEART && !hasAdvancedHeartServiceCode) {
          return false;
        }

        if (typeId === CWF_DOCUMENT_TYPE_NAMES.ADVANCE_NEURO && !hasAdvancedNeuroServiceCode) {
          return false;
        }

        if (typeId === CWF_DOCUMENT_TYPE_NAMES.BODY_COMPOSITION && !hasBodyCompositionServiceCode) {
          return false;
        }
      }

      return true;
    });
}

interface FilterAndSortDocumentsByTypeIdArgs {
  acceptedTypeIds: (string | undefined)[];
  documents: CwfActiveDocument[];
  types: CwfDocumentType[];
}

function filterAndSortDocumentsByTypeIds({
  acceptedTypeIds,
  documents,
  types,
}: FilterAndSortDocumentsByTypeIdArgs) {
  return documents
    .filter(({ typeId }) => acceptedTypeIds.includes(typeId))
    .filter(({ status }) => !NON_VIEWABLE_STATUSES.includes(status))
    .sort((a, b) => {
      const aName = types.find(({ uuid }) => uuid === a.typeId)?.name;
      const bName = types.find(({ uuid }) => uuid === b.typeId)?.name;

      if (aName && bName) {
        return aName.localeCompare(bName);
      }

      return 0;
    });
}

interface GeneratePatientDocsByStudyIdArgs {
  docs: CwfActiveDocument[];
  studies: StudyResult[];
  studyId: string;
  types: CwfDocumentType[];
}

export function generatePatientDocsByStudyId({
  docs,
  studies,
  studyId,
  types,
}: GeneratePatientDocsByStudyIdArgs): DocsByStudyId {
  return studies.reduce((acc, study) => {
    const docsByStudyId = docs.filter((doc) => doc.studyId === study.id);

    const studyServicesInfo = study.poetryMetadata?.servicesInfo ?? [];
    const acceptedTypeIds = determineAcceptedTypeIds({
      servicesInfo: studyServicesInfo,
      sku: study.skuId,
      types,
    });

    const documents = filterAndSortDocumentsByTypeIds({
      acceptedTypeIds,
      documents: docsByStudyId,
      types,
    });

    return {
      ...acc,
      [study.id]: {
        documents,
        mhsLink: generateMhsLink(study.id),
        radiologyReportLink:
          studyId === study.id ? undefined : generateRadiologistReportPdfLink(study.id),
        study,
      },
    };
  }, {} as DocsByStudyId);
}

interface GeneratePatientDocsByCArePlanIdArgs {
  carePlanId: string;
  docs: CwfActiveDocument[];
  poetryMetadata?: PoetryMetadata;
  scanDate: number;
  servicesInfo: ServicesInfo[];
  sku: Sku;
  studyId: string;
  timezone?: string;
  types: CwfDocumentType[];
}

export function generatePatientDocsByCarePlanId({
  carePlanId,
  docs,
  poetryMetadata,
  scanDate,
  servicesInfo,
  sku,
  studyId,
  timezone,
  types,
}: GeneratePatientDocsByCArePlanIdArgs): DocsByStudy {
  const docsByCarePlanId = docs.filter((doc) => doc.carePlanId === carePlanId);

  const acceptedTypeIds = determineAcceptedTypeIds({ servicesInfo, sku, types });

  const documents = filterAndSortDocumentsByTypeIds({
    acceptedTypeIds,
    documents: docsByCarePlanId,
    types,
  });

  return {
    documents,
    mhsLink: generateMhsLink(studyId),
    study: { id: studyId, date: scanDate, poetryMetadata, skuId: sku, timezone },
  };
}
